/**
 * Ensures the quantity of Water bottle product in the cart always matches
 * the total quantity of all trigger products (backpacks), using SKUs.
 *
 * Listens for 'rebuy:cart.change' (Rebuy Smart Cart).
 */

// === CONFIGURATION ===

// List all SKUs for all trigger products (backpacks)
const BACKPACK_SKUS = ['BPDRMCHSR', 'BPFRSZN2BBLMN', 'BPNGHTGMSZN2', 'BPGRNDSLMSZN2', 'BACKPACKCCC_CCC_TEST', 'BPBANANEON', 'BPCRYNCCGD', 'BPSPLTRGX', 'BPCKBD', 'BPPNCL', 'BPTM2SHN', 'BPGRAFBK', 'BPRTOGRN', 'BPFRSTR', 'BPBSBLK', 'BPBSWHT', 'BPFRCC'];

// SKU for Water bottle product
const WATER_BOTTLE_ID = '8264606417078';
const WATER_BOTTLE_VARIANT_ID = "44140881838262";

let isSyncing = false;
let syncTimeout = null;
let syncDebounceTimeout = null;

/**
 * Counts the total quantity of all Collection A products in the cart (by SKU).
 * @param {Array} cartItems - The cart items array.
 * @returns {number} The total quantity.
 */
function getCollectionAQuantity(cartItems) {
  return cartItems.reduce((sum, item) =>
    BACKPACK_SKUS.includes(item.sku) ? sum + item.quantity : sum
  , 0);
}

/**
 * Gets all cart items for Product B (water bottle) by variant ID.
 * @param {Array} cartItems - The cart items array.
 * @returns {Array} Array of Product B cart items.
 */
function getAllProductBItems(cartItems) {
  return cartItems.filter(item => String(item.id) === String(WATER_BOTTLE_VARIANT_ID));
}

/**
 * Gets the total quantity of all Product B (water bottle) line items.
 * @param {Array} cartItems - The cart items array.
 * @returns {number} The total quantity.
 */
function getProductBTotalQuantity(cartItems) {
  return getAllProductBItems(cartItems).reduce((sum, item) => sum + item.quantity, 0);
}

/**
 * Returns true if there is at most one water bottle line item in the cart.
 */
function isCartSettled(cartItems) {
  return getAllProductBItems(cartItems).length <= 1;
}

/**
 * Syncs Product B's quantity to match the total Collection A quantity using Rebuy Cart methods.
 * @returns {Promise<void>}
 */
async function syncProductBToCollectionA(event) {
  if (isSyncing) return;
  // Use Rebuy Cart object from event.detail.cart
  const Cart = event?.detail?.cart;
  if (!Cart) {
    console.log('Rebuy Cart object not found on event.detail.cart');
    isSyncing = false;
    return;
  }

  // Validate water bottle availability before proceeding
  const inventory = await window.getProductInventory([WATER_BOTTLE_ID]);
  console.log('inventory: ', inventory);
  if (!inventory || !inventory[WATER_BOTTLE_ID] || inventory[WATER_BOTTLE_ID].totalInventory <= 5 || !inventory[WATER_BOTTLE_ID].publishedAt) {
    console.log('Water bottle product not available or out of stock, skipping sync');
    isSyncing = false;
    return;
  }

  // Get all items in the cart
  const cartItems = Cart.getItems ? Cart.getItems() : (Cart.items ? Cart.items() : []);
  console.log('cartItems: ', cartItems);

  const collectionAQty = getCollectionAQuantity(cartItems);
  const productBItems = getAllProductBItems(cartItems);
  const currentWaterBottleQty = productBItems.reduce((sum, item) => sum + item.quantity, 0);

  // Limit water bottles to maximum of 3
  const maxWaterBottles = 3;
  const targetWaterBottleQty = Math.min(collectionAQty, maxWaterBottles);

  if (currentWaterBottleQty === targetWaterBottleQty) {
    isSyncing = false;
    return;
  }

  isSyncing = true;
  try {
    // Update the first water bottle line item to the correct total if needed
    if (productBItems.length > 0) {
      if (productBItems[0].quantity !== targetWaterBottleQty) {
        await Cart.changeItem({ id: productBItems[0].key, quantity: targetWaterBottleQty });
      }
      // Remove any extra water bottle line items (the split ones) using their line item keys
      for (let i = 1; i < productBItems.length; i++) {
        await Cart.changeItem({ id: productBItems[i].key, quantity: 0 });
      }
    } else if (targetWaterBottleQty > 0) {
      // No water bottles in cart, add one
      await Cart.addItem({ id: WATER_BOTTLE_VARIANT_ID, quantity: targetWaterBottleQty });
    }
  } catch (err) {
    console.error('Error syncing Product B with Collection A:', err);
  } finally {
    isSyncing = false;
  }
}

/**
 * Gets the variant ID for a given SKU from the cart items.
 * If not found, returns null.
 * @param {string} sku - The SKU to search for.
 * @param {Array} cartItems - The cart items array.
 * @returns {number|null}
 */
function getVariantIdBySku(sku, cartItems) {
  const item = cartItems.find(item => item.sku === sku);
  return item ? item.variant_id : null;
}

/**
 * Sets up the event listener for Rebuy Smart Cart changes.
 */
function setupRebuyCartSync() {
  document.addEventListener('rebuy:cart.change', function(event) {
    if (syncDebounceTimeout) clearTimeout(syncDebounceTimeout);
    syncDebounceTimeout = setTimeout(() => {
      syncProductBToCollectionA(event);
    }, 300); // 300ms debounce
  });
}

document.addEventListener('rebuy:cart.ready', setupRebuyCartSync);
